import { urlDoSelo } from 'components/icons';
import { DestaqueDoFunci } from 'services/destaques.service';
import { BehaviorLabel } from 'utils/strings';

export type TipoDeDestaque = 'galeria' | 'cultura' | 'realizacao';

/** Ordem dos selos no cartão e das seções no perfil (`Galeria.dc.html`). */
export const TIPOS_DE_DESTAQUE: TipoDeDestaque[] = ['galeria', 'cultura', 'realizacao'];

// Nome da arte em `urlDoSelo` por comportamento da Cultura.
const ARTE_DA_CULTURA: Record<string, string> = {
  colaboracao: 'Colaboracao',
  foco: 'Foco',
  inovacao: 'Inovacao',
  resolutividade: 'Resolutividade',
  lideranca: 'Lideranca',
};

const rotuloDoComportamento = (comportamento?: string | null) => BehaviorLabel[comportamento ?? ''] ?? comportamento;

interface RegrasDoTipo {
  /** Título da seção no perfil. */
  titulo: string;
  conquistou: (funci: DestaqueDoFunci) => boolean;
  /** Nome por extenso (na Cultura, com o comportamento). */
  nome: (comportamento?: string | null) => string;
  /** Arte do selo no ciclo; ciclo sem a arte, `undefined`. */
  arte: (ciclo: string, comportamento?: string | null) => string | undefined;
  /** DST-22, DST-23, DST-26: o critério, abaixo do título da seção. */
  criterio: (ciclo: string, comportamento?: string | null) => string;
}

/** Tudo o que muda por tipo de destaque, num lugar só: um tipo novo é uma entrada nova. */
const REGRAS_DO_TIPO: Record<TipoDeDestaque, RegrasDoTipo> = {
  galeria: {
    titulo: 'Galeria do Reconhecimento',
    conquistou: funci => funci.galeria,
    nome: () => 'Galeria do Reconhecimento',
    arte: ciclo => urlDoSelo('Galeria', ciclo),
    criterio: ciclo => `Entre os 30% mais bem classificados do grupo em Pontos Joga Junto no ciclo ${ciclo}.`,
  },
  cultura: {
    titulo: 'Destaque da Cultura',
    conquistou: funci => !!funci.cultura,
    nome: comportamento =>
      comportamento ? `Destaque da Cultura em ${rotuloDoComportamento(comportamento)}` : 'Destaque da Cultura',
    // Sem Cultura conquistada, a arte de Colaboração (o cartão a mostra em cinza: não há arte genérica da Cultura).
    arte: (ciclo, comportamento) => urlDoSelo(ARTE_DA_CULTURA[comportamento ?? ''] ?? ARTE_DA_CULTURA.colaboracao, ciclo),
    criterio: (ciclo, comportamento) =>
      `Entre os funcis mais reconhecidos em ${rotuloDoComportamento(comportamento)} no ciclo ${ciclo}.`,
  },
  realizacao: {
    titulo: 'Realização Destaque',
    conquistou: funci => !!funci.realizacao,
    nome: () => 'Realização Destaque',
    arte: ciclo => urlDoSelo('Realizacao', ciclo),
    criterio: ciclo => `Realizações apreciadas pela liderança e bonificadas com selo no ciclo ${ciclo}.`,
  },
};

export function tiposConquistados(funci: DestaqueDoFunci): TipoDeDestaque[] {
  return TIPOS_DE_DESTAQUE.filter(tipo => REGRAS_DO_TIPO[tipo].conquistou(funci));
}

export const tituloDoTipo = (tipo: TipoDeDestaque) => REGRAS_DO_TIPO[tipo].titulo;

export const nomeDoTipo = (tipo: TipoDeDestaque, comportamento?: string | null) => REGRAS_DO_TIPO[tipo].nome(comportamento);

export const arteDoSelo = (tipo: TipoDeDestaque, ciclo: string, comportamento?: string | null) =>
  REGRAS_DO_TIPO[tipo].arte(ciclo, comportamento);

export const criterioDoSelo = (tipo: TipoDeDestaque, ciclo: string, comportamento?: string | null) =>
  REGRAS_DO_TIPO[tipo].criterio(ciclo, comportamento);

const COR_POR_QUANTIDADE: Record<number, string> = {
  1: 'bg-brand-purple-light',
  2: 'bg-brand-green-light',
  3: 'bg-brand-yellow',
};

/** DST-07: o fundo do cartão pela quantidade de tipos conquistados. */
export function corDoCartao(quantidade: number) {
  return COR_POR_QUANTIDADE[quantidade] ?? COR_POR_QUANTIDADE[1];
}

/** "A", "A e B", "A, B e C". */
function emLista(itens: string[]) {
  return itens.length <= 1 ? itens.join('') : `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
}

/** O nome que o cartão e o perfil mostram: o nome de guerra, ou o nome sem ele. */
export function nomeExibido(funci: Pick<DestaqueDoFunci, 'nome' | 'nome_guerra'>) {
  return funci.nome_guerra || funci.nome;
}

/**
 * DST-07: os destaques conquistados, por extenso ("Galeria do Reconhecimento e Destaque da Cultura em Foco no
 * Cliente"). No cartão, é o texto só para o leitor de tela: as artes são decorativas (DST-43).
 */
export function destaquesPorExtenso(funci: DestaqueDoFunci) {
  return emLista(tiposConquistados(funci).map(tipo => nomeDoTipo(tipo, funci.cultura)));
}
